/**
 * Search drawer (snippets/search-drawer.liquid).
 *
 * - [data-search-open] links (header / mobile menu search icons) open the drawer
 *   instead of navigating to /search.
 * - Typing fetches /search/suggest rendered by sections/predictive-search.liquid
 *   and swaps it into the drawer; an empty field shows "Popular collections".
 * - Closes on the X, the overlay or Escape; focus is kept inside while open.
 */
class SearchDrawer extends HTMLElement {

  constructor() {
    super();
    this.abortController = null;
    this.debounceTimer = null;
    this.returnFocusTo = null;
  };

  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.body = this.querySelector('[data-search-body]');
    this.results = this.querySelector('[data-search-results]');

    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('click', this.onClick);
    if (this.input) this.input.addEventListener('input', this.onInput);
  };

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('click', this.onClick);
    if (this.input) this.input.removeEventListener('input', this.onInput);
    if (this.abortController) this.abortController.abort();
    window.clearTimeout(this.debounceTimer);
  };

  get isOpen() { return this.dataset.open === 'true'; };

  onDocumentClick = (event) => {
    const trigger = event.target.closest('[data-search-open]');
    if (!trigger) return;
    event.preventDefault();

    // The mobile menu sits above the page; close it before showing search.
    if (document.body.getAttribute('data-nav-open') === 'true') {
      const navTrigger = document.querySelector('mobile-nav-trigger');
      if (navTrigger && typeof navTrigger.toggle === 'function') navTrigger.toggle();
    }
    this.open();
  };

  onClick = (event) => {
    if (event.target.closest('[data-search-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target.closest('[data-search-clear]')) {
      event.preventDefault();
      this.input.value = '';
      this.showResults('');
      this.input.focus();
    }
  };

  open() {
    if (this.isOpen) return;
    this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    this.dataset.open = 'true';
    document.body.setAttribute('data-overlay-active', 'true');
    document.addEventListener('keydown', this.onKeydown);
    // Focus once the panel has started sliding in (focusing an off-screen input can jump the page).
    window.setTimeout(() => { if (this.input) this.input.focus({ preventScroll: true }); }, 50);
  };

  close() {
    if (!this.isOpen) return;
    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');
    document.body.removeAttribute('data-overlay-active');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.returnFocusTo && this.returnFocusTo.isConnected) this.returnFocusTo.focus({ preventScroll: true });
    this.returnFocusTo = null;
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  trapFocus(event) {
    const focusables = Array.from(this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])'))
      .filter((el) => !el.hidden && el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  onInput = () => {
    const query = this.input.value.trim();
    if (this.clearButton) this.clearButton.hidden = !this.input.value.length;
    window.clearTimeout(this.debounceTimer);
    if (!query) {
      this.showResults('');
      return;
    }
    this.debounceTimer = window.setTimeout(() => this.fetchResults(query), 250);
  };

  async fetchResults(query) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,query',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
      section_id: 'predictive-search',
    });
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    try {
      const response = await fetch(`${root}search/suggest?${params.toString()}`, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Search request failed (${response.status})`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      // Ignore a late response for a query that has since been cleared.
      if (!this.input.value.trim()) return;
      this.showResults(section ? section.innerHTML : '');
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Search drawer:', error);
    }
  };

  showResults(markup) {
    if (!this.results) return;
    this.results.innerHTML = markup;
    this.body.dataset.hasResults = markup.trim() ? 'true' : 'false';
    if (this.clearButton) this.clearButton.hidden = !this.input.value.length;
    this.body.scrollTop = 0;
  };

};

if (!customElements.get('search-drawer')) customElements.define('search-drawer', SearchDrawer);
